import { Image, Link, RawHtml, Text, Video } from '~/shared/components';
import React, { useRef } from 'react';
import { M75MediaAndTextModule } from '~/lib/data-contract';
import { useModuleTheme } from '../../hooks/useModuleTheme';
import { AlignContent } from '../AlignContent';
import { ModuleContainer } from '../ModuleContainer';
import { queries } from '~/theme/breakpoints';
import { Button } from '~/shared/components/Button/Button';
import { useIntersection } from 'react-use';
import styles from './M75MediaText.module.scss';
import clsx from 'clsx';
import { extractVideoMetadataFromModule } from '~/shared/components/Video/get-video-meta-data';
import { extractVideoSource } from '~/shared/components/Video/get-video-source';

export type M75MediaTextProps = M75MediaAndTextModule;

export const M75MediaText = ({
    image,
    imageAltText,
    video,
    headline,
    subHeadline,
    subSubHeadline,
    text,
    callToAction,
    spacingBottom,
    spacingTop,
    priority,
    color,
    mediaSize = 'default',
    mediaHorizontalAlignment = 'left',
    horizontalAlignment = 'left',
    verticalAlignment = 'center',
    alternativeBackgroundColor,
    alternativeTextColor,
    ...rest
}: M75MediaTextProps) => {
    const { backgroundColor, textColor } = useModuleTheme(rest);
    const ref = useRef<HTMLDivElement>(null);
    const shouldLoad = useIntersection(ref, { root: null, rootMargin: '0px' });
    const videoSource = extractVideoSource(video, rest);

    const mediaType = video || videoSource ? 'video' : 'image';

    return (
        <section
            className={clsx(styles.component, {
                [styles['spacing-top-' + spacingTop]]: spacingTop && spacingTop !== 'none',
                [styles['spacing-bottom-' + spacingBottom]]:
                    spacingBottom && spacingBottom !== 'none',
            })}
        >
            <ModuleContainer spacingBottom={'none'} spacingTop={'none'} {...rest}>
                <div className={styles.container}>
                    <div
                        className={clsx(styles.content, styles[mediaHorizontalAlignment], {
                            [styles.mediaSmall]: mediaSize === 'small',
                        })}
                    >
                        <AlignContent
                            maxWidth="500px"
                            className={styles.alignContent}
                            alignment={horizontalAlignment}
                            position={verticalAlignment}
                        >
                            {headline && (
                                <Text
                                    children={headline.text}
                                    as={headline.tag}
                                    variant={headline.style}
                                    color={headline?.textColor || textColor}
                                />
                            )}
                            {subHeadline && (
                                <Text
                                    children={subHeadline.text}
                                    as={subHeadline.tag}
                                    variant={subHeadline.style}
                                    color={subHeadline?.textColor || textColor}
                                />
                            )}
                            {subSubHeadline && (
                                <Text
                                    children={subSubHeadline.text}
                                    as={subSubHeadline.tag}
                                    variant={subSubHeadline.style}
                                    color={subSubHeadline?.textColor || textColor}
                                />
                            )}
                            {text ? <RawHtml html={text} /> : null}
                            <div className={styles.actions}>
                                {callToAction && (
                                    <Button
                                        variant={color}
                                        title={callToAction.title}
                                        textColor={textColor}
                                        backgroundColor={backgroundColor}
                                        alternativeBackgroundColor={alternativeBackgroundColor}
                                        alternativeTextColor={alternativeTextColor}
                                        as={Link}
                                        href={callToAction.url}
                                        children={callToAction.text}
                                        target={callToAction.target}
                                    />
                                )}
                            </div>
                        </AlignContent>

                        <div className={styles.gridArea}>
                            <div
                                ref={ref}
                                className={clsx(styles.media, styles[mediaHorizontalAlignment])}
                            >
                                {mediaType === 'image' && image ? (
                                    callToAction ? (
                                        <Link
                                            href={callToAction.url}
                                            title={callToAction.title}
                                            target={callToAction.target}
                                        >
                                            <Image
                                                {...image}
                                                layout="responsive"
                                                alt={image.alt || imageAltText}
                                                sizes={`${queries.sm} 50vw, ${queries.contentMax} 50vw, 720px`}
                                                priority={Boolean(priority)}
                                            />
                                        </Link>
                                    ) : (
                                        <Image
                                            {...image}
                                            layout="responsive"
                                            alt={image.alt || imageAltText}
                                            sizes={`${queries.sm} 50vw, ${queries.contentMax} 50vw, 720px`}
                                            priority={Boolean(priority)}
                                        />
                                    )
                                ) : null}
                                {videoSource && (shouldLoad?.isIntersecting || priority) ? (
                                    <Video
                                        metadata={extractVideoMetadataFromModule(rest)}
                                        name={headline?.text}
                                        controls={false}
                                        src={videoSource.src}
                                        loop={videoSource.loop}
                                        playing={videoSource.autoPlay}
                                        posterSrc={image?.src}
                                        muted={videoSource.mute}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </ModuleContainer>
        </section>
    );
};
